import { useRef, useState, useEffect } from 'react'
import { Camera } from 'lucide-react'
import { imageApi, siteApi } from '@/lib/api'
import { useToast } from '@/stores/toast-store'
import { ImageCropDialog } from '@/components/ui/image-crop-dialog'

const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5 MB

const TABS = ['Media', 'Profil', 'Sosmed'] as const
type Tab = typeof TABS[number]

export default function AboutSettingsPage() {
  const { toast } = useToast()

  const [activeTab, setActiveTab] = useState<Tab>('Media')

  // ── Media state ─────────────────────────────────────────────────────────────
  const photoFileInputRef  = useRef<HTMLInputElement>(null)
  const bannerFileInputRef = useRef<HTMLInputElement>(null)

  const [currentPhotoUrl,  setCurrentPhotoUrl]  = useState<string | null>(null)
  const [currentBannerUrl, setCurrentBannerUrl] = useState<string | null>(null)
  const [photoPreviewUrl,  setPhotoPreviewUrl]  = useState<string | null>(null)
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string | null>(null)
  const [photoBlob,  setPhotoBlob]  = useState<Blob | null>(null)
  const [bannerBlob, setBannerBlob] = useState<Blob | null>(null)
  const [isPhotoSubmitting,  setIsPhotoSubmitting]  = useState(false)
  const [isBannerSubmitting, setIsBannerSubmitting] = useState(false)

  // ── Crop dialog state (shared) ───────────────────────────────────────────────
  const [cropSrcUrl,  setCropSrcUrl]  = useState<string | null>(null)
  const [cropAspect,  setCropAspect]  = useState(1)
  const [cropTarget,  setCropTarget]  = useState<'photo' | 'banner'>('photo')

  // ── Text fields state ────────────────────────────────────────────────────────
  const [name,      setName]      = useState('')
  const [position,  setPosition]  = useState('')
  const [websiteUrl, setWebsiteUrl] = useState('')
  const [email,     setEmail]     = useState('')
  const [location,  setLocation]  = useState('')
  const [instagram, setInstagram] = useState('')
  const [twitter,   setTwitter]   = useState('')
  const [tiktok,    setTiktok]    = useState('')
  const [facebook,  setFacebook]  = useState('')
  const [spotify,   setSpotify]   = useState('')
  const [waChannel, setWaChannel] = useState('')
  const [isTextSubmitting, setIsTextSubmitting] = useState(false)

  // Fetch data awal
  useEffect(() => {
    siteApi.getSettings()
      .then((s) => {
        setCurrentPhotoUrl(s.about_photo_url)
        setCurrentBannerUrl(s.about_banner_url)
        setName(s.about_name ?? '')
        setPosition(s.about_position ?? '')
        setWebsiteUrl(s.about_website_url ?? '')
        setEmail(s.about_email ?? '')
        setLocation(s.about_location ?? '')
        setInstagram(s.about_instagram ?? '')
        setTwitter(s.about_twitter ?? '')
        setTiktok(s.about_tiktok ?? '')
        setFacebook(s.about_facebook ?? '')
        setSpotify(s.about_spotify ?? '')
        setWaChannel(s.about_wa_channel ?? '')
      })
      .catch(() => {})
  }, [])

  // Revoke blob URLs on unmount
  useEffect(() => {
    return () => {
      if (photoPreviewUrl?.startsWith('blob:'))  URL.revokeObjectURL(photoPreviewUrl)
      if (bannerPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(bannerPreviewUrl)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ── File change handlers ─────────────────────────────────────────────────────
  function openCrop(
    e: React.ChangeEvent<HTMLInputElement>,
    target: 'photo' | 'banner',
    aspect: number,
  ) {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast({ title: 'File tidak valid', description: 'Pilih file gambar (JPG, PNG, GIF, dll)', variant: 'destructive' })
      return
    }
    if (file.size > MAX_FILE_SIZE) {
      toast({ title: 'File terlalu besar', description: 'Ukuran maksimal adalah 5 MB', variant: 'destructive' })
      return
    }

    if (cropSrcUrl) URL.revokeObjectURL(cropSrcUrl)
    setCropTarget(target)
    setCropAspect(aspect)
    setCropSrcUrl(URL.createObjectURL(file))
    e.target.value = ''
  }

  function handleCropConfirm(blob: Blob) {
    if (cropSrcUrl) URL.revokeObjectURL(cropSrcUrl)
    setCropSrcUrl(null)

    if (cropTarget === 'photo') {
      if (photoPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(photoPreviewUrl)
      setPhotoBlob(blob)
      setPhotoPreviewUrl(URL.createObjectURL(blob))
    } else {
      if (bannerPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(bannerPreviewUrl)
      setBannerBlob(blob)
      setBannerPreviewUrl(URL.createObjectURL(blob))
    }
  }

  function handleCropCancel() {
    if (cropSrcUrl) URL.revokeObjectURL(cropSrcUrl)
    setCropSrcUrl(null)
  }

  // ── Submit handlers ──────────────────────────────────────────────────────────
  async function handlePhotoSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!photoBlob) return
    setIsPhotoSubmitting(true)
    try {
      const { url } = await imageApi.uploadImage(photoBlob)
      await siteApi.updateAbout({ about_photo_url: url })
      setCurrentPhotoUrl(url)
      setPhotoBlob(null)
      if (photoPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(photoPreviewUrl)
      setPhotoPreviewUrl(null)
      toast({ title: 'Foto profil berhasil diperbarui' })
    } catch (err) {
      toast({ title: 'Gagal memperbarui foto profil', description: (err as Error).message, variant: 'destructive' })
    } finally {
      setIsPhotoSubmitting(false)
    }
  }

  async function handleBannerSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!bannerBlob) return
    setIsBannerSubmitting(true)
    try {
      const { url } = await imageApi.uploadImage(bannerBlob)
      await siteApi.updateAbout({ about_banner_url: url })
      setCurrentBannerUrl(url)
      setBannerBlob(null)
      if (bannerPreviewUrl?.startsWith('blob:')) URL.revokeObjectURL(bannerPreviewUrl)
      setBannerPreviewUrl(null)
      toast({ title: 'Banner berhasil diperbarui' })
    } catch (err) {
      toast({ title: 'Gagal memperbarui banner', description: (err as Error).message, variant: 'destructive' })
    } finally {
      setIsBannerSubmitting(false)
    }
  }

  async function handleTextSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsTextSubmitting(true)
    try {
      await siteApi.updateAbout({
        about_name:        name.trim() || undefined,
        about_position:    position.trim() || undefined,
        about_website_url: websiteUrl.trim() || undefined,
        about_email:       email.trim() || undefined,
        about_location:    location.trim() || undefined,
        about_instagram:   instagram.trim() || undefined,
        about_twitter:     twitter.trim() || undefined,
        about_tiktok:      tiktok.trim() || undefined,
        about_facebook:    facebook.trim() || undefined,
        about_spotify:     spotify.trim() || undefined,
        about_wa_channel:  waChannel.trim() || undefined,
      })
      toast({ title: 'Data about berhasil diperbarui' })
    } catch (err) {
      toast({ title: 'Gagal memperbarui data about', description: (err as Error).message, variant: 'destructive' })
    } finally {
      setIsTextSubmitting(false)
    }
  }

  const photoDisplay  = photoPreviewUrl  ?? currentPhotoUrl
  const bannerDisplay = bannerPreviewUrl ?? currentBannerUrl

  const fieldClass = 'w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50'

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">About</h1>
        <p className="text-sm text-muted-foreground">Atur nama, posisi, media, dan akun sosmed yang tampil di homepage.</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 border-b border-border">
        {TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setActiveTab(t)}
            className={[
              'px-4 py-2 text-sm font-medium -mb-px border-b-2 transition-colors',
              activeTab === t
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            ].join(' ')}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── Tab: Media ── */}
      {activeTab === 'Media' && (
        <div className="space-y-8">
          {/* Foto Profil */}
          <form onSubmit={handlePhotoSubmit} className="max-w-lg space-y-4 rounded-xl border border-border bg-card p-6">
            <div>
              <p className="text-sm font-medium">Foto Profil</p>
              <p className="text-xs text-muted-foreground mt-0.5">Rasio 1:1 (persegi).</p>
            </div>

            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => photoFileInputRef.current?.click()}
                disabled={isPhotoSubmitting}
                aria-label="Ganti foto profil"
                className="group relative h-24 w-24 shrink-0 overflow-hidden rounded-full border border-input bg-muted focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              >
                {photoDisplay ? (
                  <img src={photoDisplay} alt="Foto profil" className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                    Belum ada foto
                  </div>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-full">
                  <Camera className="h-5 w-5 text-white" />
                </div>
              </button>

              <div className="space-y-1">
                <button
                  type="button"
                  onClick={() => photoFileInputRef.current?.click()}
                  disabled={isPhotoSubmitting}
                  className="text-sm font-medium text-primary hover:underline disabled:opacity-50 disabled:no-underline"
                >
                  Pilih foto
                </button>
                <p className="text-xs text-muted-foreground">JPG, PNG, GIF, WebP · Maks. 5 MB</p>
              </div>
            </div>

            <input
              ref={photoFileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => openCrop(e, 'photo', 1)}
            />

            <button
              type="submit"
              disabled={isPhotoSubmitting || !photoBlob}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            >
              {isPhotoSubmitting ? 'Menyimpan...' : 'Simpan Foto Profil'}
            </button>
          </form>

          {/* Banner */}
          <form onSubmit={handleBannerSubmit} className="max-w-lg space-y-4 rounded-xl border border-border bg-card p-6">
            <div>
              <p className="text-sm font-medium">Banner</p>
              <p className="text-xs text-muted-foreground mt-0.5">Rasio 16:9 (landscape).</p>
            </div>

            <button
              type="button"
              onClick={() => bannerFileInputRef.current?.click()}
              disabled={isBannerSubmitting}
              aria-label="Ganti banner"
              className="group relative w-full overflow-hidden rounded-lg border border-input bg-muted focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50"
              style={{ aspectRatio: '16/9' }}
            >
              {bannerDisplay ? (
                <img src={bannerDisplay} alt="Banner" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-sm text-muted-foreground">
                  Belum ada banner
                </div>
              )}
              <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                <Camera className="h-5 w-5 text-white" />
                <span className="text-sm font-medium text-white">Ganti foto</span>
              </div>
            </button>

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => bannerFileInputRef.current?.click()}
                disabled={isBannerSubmitting}
                className="text-sm font-medium text-primary hover:underline disabled:opacity-50 disabled:no-underline"
              >
                Pilih foto
              </button>
              <span className="text-xs text-muted-foreground">JPG, PNG, GIF, WebP · Maks. 5 MB</span>
            </div>

            <input
              ref={bannerFileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => openCrop(e, 'banner', 16 / 9)}
            />

            <button
              type="submit"
              disabled={isBannerSubmitting || !bannerBlob}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
            >
              {isBannerSubmitting ? 'Menyimpan...' : 'Simpan Banner'}
            </button>
          </form>
        </div>
      )}

      {/* ── Tab: Profil ── */}
      {activeTab === 'Profil' && (
        <form onSubmit={handleTextSubmit} className="max-w-lg space-y-5 rounded-xl border border-border bg-card p-6">
          {/* Nama */}
          <div className="space-y-1.5">
            <label htmlFor="about-name" className="text-sm font-medium">Nama</label>
            <input
              id="about-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Oktavia Dwi Anggraeni"
              maxLength={100}
              disabled={isTextSubmitting}
              className={fieldClass}
            />
          </div>

          {/* Posisi */}
          <div className="space-y-1.5">
            <label htmlFor="about-position" className="text-sm font-medium">Posisi / Jabatan</label>
            <input
              id="about-position"
              type="text"
              value={position}
              onChange={(e) => setPosition(e.target.value)}
              placeholder="Social Media Specialist"
              maxLength={100}
              disabled={isTextSubmitting}
              className={fieldClass}
            />
          </div>

          <hr className="border-border" />
          <p className="text-sm font-medium">Kontak & Lainnya</p>

          {/* Website URL */}
          <div className="space-y-1.5">
            <label htmlFor="about-website" className="text-sm font-medium text-muted-foreground">Website URL</label>
            <input id="about-website" type="url" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://example.com" disabled={isTextSubmitting} className={fieldClass} />
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <label htmlFor="about-email" className="text-sm font-medium text-muted-foreground">Email</label>
            <input id="about-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="hello@example.com" disabled={isTextSubmitting} className={fieldClass} />
          </div>

          {/* Lokasi */}
          <div className="space-y-1.5">
            <label htmlFor="about-location" className="text-sm font-medium text-muted-foreground">Lokasi</label>
            <input id="about-location" type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Jakarta, Indonesia" maxLength={100} disabled={isTextSubmitting} className={fieldClass} />
            <p className="text-xs text-muted-foreground">Ditampilkan sebagai ikon pin di homepage.</p>
          </div>

          <button
            type="submit"
            disabled={isTextSubmitting}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            {isTextSubmitting ? 'Menyimpan...' : 'Simpan'}
          </button>
        </form>
      )}

      {/* ── Tab: Sosmed ── */}
      {activeTab === 'Sosmed' && (
        <form onSubmit={handleTextSubmit} className="max-w-lg space-y-5 rounded-xl border border-border bg-card p-6">
          {/* Instagram */}
          <div className="space-y-1.5">
            <label htmlFor="about-instagram" className="text-sm font-medium text-muted-foreground">Instagram</label>
            <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
              <span className="select-none px-3 text-sm text-muted-foreground">instagram.com/</span>
              <input id="about-instagram" type="text" value={instagram} onChange={(e) => setInstagram(e.target.value)} placeholder="username" maxLength={100} disabled={isTextSubmitting} className="flex-1 bg-transparent py-2 pr-3 text-sm focus:outline-none disabled:opacity-50" />
            </div>
          </div>

          {/* Twitter */}
          <div className="space-y-1.5">
            <label htmlFor="about-twitter" className="text-sm font-medium text-muted-foreground">Twitter / X</label>
            <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
              <span className="select-none px-3 text-sm text-muted-foreground">x.com/</span>
              <input id="about-twitter" type="text" value={twitter} onChange={(e) => setTwitter(e.target.value)} placeholder="username" maxLength={100} disabled={isTextSubmitting} className="flex-1 bg-transparent py-2 pr-3 text-sm focus:outline-none disabled:opacity-50" />
            </div>
          </div>

          {/* TikTok */}
          <div className="space-y-1.5">
            <label htmlFor="about-tiktok" className="text-sm font-medium text-muted-foreground">TikTok</label>
            <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
              <span className="select-none px-3 text-sm text-muted-foreground">tiktok.com/@</span>
              <input id="about-tiktok" type="text" value={tiktok} onChange={(e) => setTiktok(e.target.value)} placeholder="username" maxLength={100} disabled={isTextSubmitting} className="flex-1 bg-transparent py-2 pr-3 text-sm focus:outline-none disabled:opacity-50" />
            </div>
          </div>

          {/* Facebook */}
          <div className="space-y-1.5">
            <label htmlFor="about-facebook" className="text-sm font-medium text-muted-foreground">Facebook</label>
            <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
              <span className="select-none px-3 text-sm text-muted-foreground">facebook.com/</span>
              <input id="about-facebook" type="text" value={facebook} onChange={(e) => setFacebook(e.target.value)} placeholder="username" maxLength={100} disabled={isTextSubmitting} className="flex-1 bg-transparent py-2 pr-3 text-sm focus:outline-none disabled:opacity-50" />
            </div>
          </div>

          {/* Spotify */}
          <div className="space-y-1.5">
            <label htmlFor="about-spotify" className="text-sm font-medium text-muted-foreground">Spotify</label>
            <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
              <span className="select-none px-3 text-sm text-muted-foreground">open.spotify.com/user/</span>
              <input id="about-spotify" type="text" value={spotify} onChange={(e) => setSpotify(e.target.value)} placeholder="username atau ID" maxLength={200} disabled={isTextSubmitting} className="flex-1 bg-transparent py-2 pr-3 text-sm focus:outline-none disabled:opacity-50" />
            </div>
          </div>

          {/* WhatsApp Channel */}
          <div className="space-y-1.5">
            <label htmlFor="about-wa-channel" className="text-sm font-medium text-muted-foreground">WhatsApp Channel</label>
            <div className="flex items-center rounded-md border border-input bg-background focus-within:ring-2 focus-within:ring-ring">
              <span className="select-none px-3 text-sm text-muted-foreground">whatsapp.com/channel/</span>
              <input id="about-wa-channel" type="text" value={waChannel} onChange={(e) => setWaChannel(e.target.value)} placeholder="ID channel" maxLength={200} disabled={isTextSubmitting} className="flex-1 bg-transparent py-2 pr-3 text-sm focus:outline-none disabled:opacity-50" />
            </div>
          </div>

          <button
            type="submit"
            disabled={isTextSubmitting}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            {isTextSubmitting ? 'Menyimpan...' : 'Simpan'}
          </button>
        </form>
      )}

      {/* Crop dialog — shared untuk foto profil dan banner */}
      <ImageCropDialog
        imageSrc={cropSrcUrl}
        aspect={cropAspect}
        onConfirm={handleCropConfirm}
        onCancel={handleCropCancel}
      />
    </div>
  )
}
